// RAG settings popover: pick which uploaded guideline documents to search (defaults to
// all, selected once on first load), and tune retrieval (similarity_threshold, top_k —
// forwarded straight through to AskDocumentQuestionStreamSpecification). Deleting a
// document here cascades through DeleteDocumentSpecification (DB row + disk file +
// Qdrant vectors).
import { useEffect, useRef, useState } from "react"
import {
    DeleteDocumentSpecification,
    Document as GuidelineDocument,
    GetDocumentsSpecification
} from "pokal_api"

type Props = {
    selectedDocumentIds: string[]
    onSelectionChange: (ids: string[]) => void
    similarityThreshold: number
    onThresholdChange: (v: number) => void
    topK: number
    onTopKChange: (v: number) => void
    onUploadClick: () => void
    onClose: () => void
}

export default function DocumentSelectorDropdown({
    selectedDocumentIds,
    onSelectionChange,
    similarityThreshold,
    onThresholdChange,
    topK,
    onTopKChange,
    onUploadClick,
    onClose,
}: Props) {
    const [documents, setDocuments] = useState<GuidelineDocument[]>([])
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        GetDocumentsSpecification.request({}).then(res => {
            const docs = res.documents ?? []
            setDocuments(docs)
            if (selectedDocumentIds.length === 0 && docs.length > 0) {
                onSelectionChange(docs.map(d => d.id))
            }
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose()
        }
        document.addEventListener("mousedown", handleClick)
        return () => document.removeEventListener("mousedown", handleClick)
    }, [onClose])

    function toggleDoc(id: string) {
        if (selectedDocumentIds.includes(id)) {
            onSelectionChange(selectedDocumentIds.filter(d => d !== id))
        } else {
            onSelectionChange([...selectedDocumentIds, id])
        }
    }

    async function deleteDoc(id: string) {
        setDeletingId(id)
        try {
            await DeleteDocumentSpecification.request({ document_id: id })
            const updated = documents.filter(d => d.id !== id)
            setDocuments(updated)
            onSelectionChange(selectedDocumentIds.filter(s => s !== id))
        } finally {
            setDeletingId(null)
        }
    }

    return (
        <div
            ref={ref}
            className="absolute top-9 right-0 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3 flex flex-col gap-3"
        >
            <div>
                <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-bold text-gray-700">Dokumente</span>
                    <button
                        onClick={() => onSelectionChange(documents.map(d => d.id))}
                        className="text-xs text-blue-500 hover:underline"
                    >
                        Alle
                    </button>
                </div>
                <div className="flex flex-col gap-1 mb-2">
                    {documents.map(doc => (
                        <div key={doc.id} className="flex items-center gap-2 bg-gray-50 rounded px-1.5 py-1">
                            <input
                                type="checkbox"
                                checked={selectedDocumentIds.includes(doc.id)}
                                onChange={() => toggleDoc(doc.id)}
                                className="accent-blue-500 w-3 h-3"
                            />
                            <span className="flex-1 text-xs text-gray-700 truncate">{doc.title}</span>
                            <button
                                onClick={() => deleteDoc(doc.id)}
                                disabled={deletingId === doc.id}
                                className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    {documents.length === 0 && (
                        <span className="text-xs text-gray-400">Keine Dokumente hochgeladen</span>
                    )}
                </div>
                <button
                    onClick={onUploadClick}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-md py-1.5 transition-colors"
                >
                    Dokument hochladen
                </button>
            </div>

            <hr className="border-gray-100" />

            <div>
                <span className="text-xs font-bold text-gray-700">Suche</span>
                <div className="mt-1">
                    <div className="text-xs text-gray-500 mb-0.5">
                        Ähnlichkeitsschwelle: <strong>{similarityThreshold.toFixed(2)}</strong>
                    </div>
                    <input
                        type="range" min={0} max={1} step={0.05}
                        value={similarityThreshold}
                        onChange={e => onThresholdChange(parseFloat(e.target.value))}
                        className="w-full accent-blue-500"
                    />
                </div>
                <div className="mt-1">
                    <div className="text-xs text-gray-500 mb-0.5">
                        Anzahl an relevanten Abschnitten: <strong>{topK}</strong>
                    </div>
                    <input
                        type="range" min={1} max={20} step={1}
                        value={topK}
                        onChange={e => onTopKChange(parseInt(e.target.value))}
                        className="w-full accent-blue-500"
                    />
                </div>
            </div>
        </div>
    )
}
